import { patchHostedIdentifySessionVaultMutation } from '@onefootprint/fetch-hosted/queries';
import { useRequestError } from '@onefootprint/request';
import { useMutation } from '@tanstack/react-query';
import noop from 'lodash/noop';
import { type JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StepHeader from '@/idv/components/step-header';
import type { Context } from '../../identify.types';
import EmailPageStructure from '../email-page-structure';
type CollectEmailProps = {
  defaultValues: {
    email?: string;
  };
  onPrev?: () => void;
  onDone: (email: string) => Promise<void>;
  onError: (location: string, error: unknown) => void;
  context: Context;
  children?: JSX.Element;
};
const CollectEmail = ({
  context,
  defaultValues,
  onPrev,
  onDone,
  onError,
  children
}: CollectEmailProps) => {
  const {
    t
  } = useTranslation('identify');
  const {
    state: {
      identifyToken
    },
    initArgs: {
      logoConfig
    }
  } = context;
  const {
    getErrorContext,
    getErrorCode,
    getErrorMessage
  } = useRequestError();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const vaultMutation = useMutation(patchHostedIdentifySessionVaultMutation({
    headers: {
      'X-Fp-Authorization': identifyToken
    }
  }));
  const handleSubmit = async (email: string) => {
    try {
      setIsSubmitting(true);
      await vaultMutation.mutateAsync({
        body: {
          'id.email': email
        }
      });
      await onDone(email);
    } catch (error) {
      const isVaultDataValidationError = getErrorCode(error) === 'T120';
      const context_0 = isVaultDataValidationError ? getErrorContext(error) : {};
      const errorMessage = context_0['id.email'] ?? getErrorMessage(error);
      onError('collect-email', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleGoBack = async () => {
    try {
      await vaultMutation.mutateAsync({
        body: {
          // @ts-expect-error: apis don't support null values
          'id.email': null
        }
      });
      onPrev?.();
    } catch (_) {
      // do nothing
    }
  };
  return <EmailPageStructure defaultEmail={defaultValues?.email} Header={({
    title,
    subtitle,
    overrideLeftButton
  }) => {
    const getLeftButton = () => {
      // TODO: check if we really need this condition
      if (overrideLeftButton) {
        return overrideLeftButton;
      }
      if (onPrev) {
        return {
          variant: 'back',
          onBack: handleGoBack
        } as const;
      }
      return {
        variant: 'close'
      } as const;
    };
    return <StepHeader leftButton={getLeftButton()} logoUrl={logoConfig?.logoUrl} orgName={logoConfig?.orgName} showLogo={!!logoConfig} subtitle={subtitle} title={title} />;
  }} Footer={children} isLoading={isSubmitting} onSubmit={isSubmitting ? noop : handleSubmit} texts={{
    // TODO: Remove this prop, and pass to the step header directly
    header: {
      title: t($ => $['email-step'].title),
      subtitle: t($_0 => $_0['email-step'].subtitle)
    },
    email: {
      invalid: t($_1 => $_1.email.errors.invalid),
      label: t($_2 => $_2.email.label),
      placeholder: t($_3 => $_3.email.placeholder),
      required: t($_4 => $_4.email.errors.required)
    },
    cta: t($_5 => $_5.continue)
  }} />;
};
export default CollectEmail;